import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Check, ChevronRight, CircleHelp, Clock, FileCode, FileText, Headphones, Layers,
  Link2, ListChecks, MessageSquareText, Newspaper, NotebookText, Pencil, PenLine, Plus, Save, ScrollText, Sparkles,
  Trash2, Upload, Video,
} from 'lucide-react';
import { topicService, lessonService, pdfService, mediaService, courseService, progressService, taskService, articleService } from '../services/api';
import { ArticleCover, ReadBadge } from '../components/articles/ArticleBits';
import { shortDate } from '../utils/articles';
import { useCourse } from '../contexts/CourseContext';
import { useAuth } from '../contexts/AuthContext';
import FlashcardViewer from '../components/FlashcardViewer';
import { AITutor } from '../components/ai/AITutor';
import { AIQuestions } from '../components/ai/AIQuestions';
import { SelectionActions } from '../components/ai/SelectionActions';
import { ListenButton } from '../components/ai/ListenButton';
import { useTutorOpen, goToSource } from '../components/ai/tutorPage';
import {
  AppShell, Badge, Breadcrumbs, Button, Card, CodeTag, Collapsible, EmptyState, Field, FileButton, IconBadge, LinkButton,
  Loader, Markdown, Modal, PageHeader, PagerNav, ProgressBar, Tabs,
} from '../components/ui';
import './TopicView.css';

const PRIORITIES = {
  0: { label: 'Baixa', tone: 'neutral' },
  1: { label: 'Média', tone: 'warning' },
  2: { label: 'Alta', tone: 'danger' },
};

function TopicView() {
  const { topicId, courseSlug } = useParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse, getPhases } = useCourse();

  // Build navigation path with course slug
  const buildPath = (path) => courseSlug ? `/curso/${courseSlug}${path}` : path;
  const [topic, setTopic] = useState(null);
  const [allTopics, setAllTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  // A página do tema abre o tópico direto na aba Artigos
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'lessons');
  const [topicArticles, setTopicArticles] = useState([]);
  const [editingArtifact, setEditingArtifact] = useState(null);
  const [artifactValue, setArtifactValue] = useState('');
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [newLesson, setNewLesson] = useState({ name: '', description: '' });
  // Tutor na lateral e questões geradas pela IA
  const [tutorOpen, setTutorOpen] = useTutorOpen();
  const [tutorRequest, setTutorRequest] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const pageRef = useRef(null);
  const [lessonProgress, setLessonProgress] = useState({});
  const [tasks, setTasks] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 0 });
  const { isAdmin, user } = useAuth();

  useEffect(() => {
    articleService.forTopic(topicId).then(setTopicArticles).catch(() => setTopicArticles([]));
  }, [topicId]);
  // Edição de tópico e aulas (admin)
  const [topicForm, setTopicForm] = useState(null);
  const [confirmDeleteTopic, setConfirmDeleteTopic] = useState(false);
  const [lessonForm, setLessonForm] = useState(null);
  const [lessonToDelete, setLessonToDelete] = useState(null);

  useEffect(() => {
    if (courseSlug) {
      selectCourse(courseSlug);
    }
    loadTopic();
  }, [topicId, courseSlug]);

  const loadTopic = async () => {
    try {
      const [topicData, topicsData] = await Promise.all([
        topicService.getTopicById(topicId, true),
        courseSlug ? courseService.getTopicsByCourse(courseSlug, false) : Promise.resolve([]),
      ]);
      setTopic(topicData);
      setAllTopics(topicsData);
      if (courseSlug) {
        loadProgress();
        loadTasks();
      }
    } catch (error) {
      console.error('Error loading topic:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const progressData = await progressService.getProgressByCourse(courseSlug);
      const progressMap = {};
      progressData.forEach(p => { progressMap[p.lessonId] = p.completed; });
      setLessonProgress(progressMap);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const loadTasks = async () => {
    try {
      const tasksData = await taskService.getTasksByTopic(topicId);
      setTasks(tasksData);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const handleToggleProgress = async (e, lessonId) => {
    e.stopPropagation();
    try {
      const result = await progressService.toggleProgress(lessonId, courseSlug);
      setLessonProgress(prev => ({ ...prev, [lessonId]: result.completed }));
    } catch (error) {
      console.error('Error toggling progress:', error);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await taskService.createTask({ ...newTask, topicId: parseInt(topicId), courseSlug });
      setNewTask({ title: '', description: '', priority: 0 });
      setShowAddTask(false);
      loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleTaskStatusChange = async (taskId, status) => {
    try {
      await taskService.updateStatus(taskId, status);
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await taskService.deleteTask(taskId);
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleSaveArtifact = async () => {
    if (!editingArtifact) return;
    try {
      await topicService.updateArtifacts(topicId, { [editingArtifact]: artifactValue });
      setTopic({ ...topic, [editingArtifact]: artifactValue });
      setEditingArtifact(null);
    } catch (error) {
      console.error('Error saving artifact:', error);
    }
  };

  const handleAddLesson = async () => {
    if (!newLesson.name.trim()) return;
    try {
      await lessonService.createLesson(newLesson, topicId);
      setNewLesson({ name: '', description: '' });
      setShowAddLesson(false);
      loadTopic();
    } catch (error) {
      console.error('Error creating lesson:', error);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await pdfService.uploadTopicPdf(file, topicId);
      await topicService.updateTopic(topicId, { pdfPath: result.filename });
      loadTopic();
    } catch (error) {
      console.error('Error uploading PDF:', error);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await mediaService.uploadTopicVideo(file, topicId);
      await topicService.updateTopic(topicId, { videoPath: result.filename });
      loadTopic();
    } catch (error) {
      console.error('Error uploading video:', error);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await mediaService.uploadTopicAudio(file, topicId);
      await topicService.updateTopic(topicId, { audioPath: result.filename });
      loadTopic();
    } catch (error) {
      console.error('Error uploading audio:', error);
    }
  };

  const askTutor = (req) => setTutorRequest({ ...req, at: Date.now() });

  const onSelection = (kind, text) => {
    if (kind === 'explain') askTutor({ message: 'Explique este trecho com outras palavras e um exemplo.', selection: text });
    else if (kind === 'quiz') askTutor({ message: 'Me faça uma pergunta sobre este trecho para eu testar se entendi.', selection: text });
    else askTutor({ mode: 'flashcards', selection: text });
  };

  // "One-page · §2" → aba de artefatos, na seção
  const onSource = (label) => {
    setActiveTab('artifacts');
    goToSource(pageRef, label);
  };

  const findNextTopic = () => {
    if (!allTopics || allTopics.length === 0) return null;
    const currentIndex = allTopics.findIndex((t) => t.id === parseInt(topicId));
    if (currentIndex >= 0 && currentIndex < allTopics.length - 1) {
      return allTopics[currentIndex + 1];
    }
    return null;
  };

  const findPrevTopic = () => {
    if (!allTopics || allTopics.length === 0) return null;
    const currentIndex = allTopics.findIndex((t) => t.id === parseInt(topicId));
    if (currentIndex > 0) {
      return allTopics[currentIndex - 1];
    }
    return null;
  };

  const flashcards = useMemo(() => parseFlashcards(topic?.flashcards), [topic?.flashcards]);
  const phase = topic ? getPhases().find(f => f.num === topic.fase) : null;
  const studyCrumb = { label: 'Modo Estudo', onClick: () => navigate(buildPath('/estudo')) };

  if (loading) {
    return (
      <AppShell section="Tópico" courseName={currentCourse?.name}>
        <Loader label="Carregando tópico..." />
      </AppShell>
    );
  }

  if (!topic) {
    return (
      <AppShell section="Tópico" courseName={currentCourse?.name}>
        <EmptyState
          file="topico.xml"
          title="Tópico não encontrado"
          description="Ele pode ter sido removido ou o link está incorreto."
          action={<Button icon={ArrowLeft} onClick={() => navigate(buildPath('/estudo'))}>Voltar para o Modo Estudo</Button>}
        />
      </AppShell>
    );
  }

  const nextTopic = findNextTopic();
  const prevTopic = findPrevTopic();
  const lessons = topic.lessons || [];
  const completedCount = lessons.filter(l => lessonProgress[l.id]).length;

  // Ações de edição comuns aos artefatos de texto
  const saveTopicForm = async () => {
    try {
      const updated = await topicService.updateTopic(topic.id, {
        name: topicForm.name.trim(), description: topicForm.description, fase: Number(topicForm.fase),
      });
      setTopic({ ...topic, name: updated.name, description: updated.description, fase: updated.fase });
      setTopicForm(null);
    } catch (error) {
      console.error('Error updating topic:', error);
    }
  };

  const deleteCurrentTopic = async () => {
    try {
      await topicService.deleteTopic(topic.id);
      navigate(buildPath('/estudo'), { replace: true });
    } catch (error) {
      console.error('Error deleting topic:', error);
    }
  };

  const saveLessonForm = async () => {
    try {
      await lessonService.updateLesson(lessonForm.id, {
        name: lessonForm.name.trim(),
        description: lessonForm.description,
        durationMinutes: lessonForm.durationMinutes === '' ? null : Number(lessonForm.durationMinutes),
      });
      setLessonForm(null);
      loadTopic();
    } catch (error) {
      console.error('Error updating lesson:', error);
    }
  };

  const deleteLesson = async () => {
    try {
      await lessonService.deleteLesson(lessonToDelete.id);
      setLessonToDelete(null);
      loadTopic();
    } catch (error) {
      console.error('Error deleting lesson:', error);
    }
  };

  const renderEditActions = (field, label = 'Editar') => !isAdmin ? null :
    editingArtifact === field ? (
      <>
        <Button size="sm" variant="ghost" onClick={() => setEditingArtifact(null)}>Cancelar</Button>
        <Button size="sm" icon={Save} onClick={handleSaveArtifact}>Salvar</Button>
      </>
    ) : (
      <Button
        size="sm"
        variant="outline"
        icon={Pencil}
        onClick={() => {
          setEditingArtifact(field);
          setArtifactValue(topic[field] || '');
        }}
      >
        {label}
      </Button>
    );

  const renderTextArtifact = (field, emptyText, placeholder, rows) =>
    editingArtifact === field ? (
      <textarea
        className="ss-textarea"
        value={artifactValue}
        onChange={(e) => setArtifactValue(e.target.value)}
        rows={rows}
        placeholder={placeholder}
      />
    ) : topic[field] ? (
      <Collapsible>
        <Markdown>{topic[field]}</Markdown>
      </Collapsible>
    ) : (
      <p className="artifact__placeholder">{emptyText}</p>
    );

  return (
    <AppShell
      section={phase ? phase.name : 'Tópico'}
      sectionNum={topic.fase}
      courseName={currentCourse?.name}
    >
      <div ref={pageRef}>
      <Breadcrumbs
        items={[
          studyCrumb,
          { label: phase ? `Fase ${phase.num}` : 'Fase' },
          { label: topic.name },
        ]}
      />

      <PageHeader
        icon={BookOpen}
        eyebrow={phase ? `Fase ${phase.num} · ${phase.name}` : undefined}
        title={topic.name}
        subtitle={topic.description}
        actions={
          <>
            {topic.pdfPath ? (
              <>
                <LinkButton
                  icon={FileText}
                  href={pdfService.getPdfUrl(topic.pdfPath)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PDF do tópico
                </LinkButton>
                {isAdmin && (
                  <FileButton variant="ghost" icon={Upload} accept=".pdf" onChange={handlePdfUpload}>
                    Trocar PDF
                  </FileButton>
                )}
              </>
            ) : isAdmin && (
              <FileButton icon={Upload} accept=".pdf" onChange={handlePdfUpload}>
                Adicionar PDF
              </FileButton>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                icon={Pencil}
                data-tour="editar-topico"
                onClick={() => setTopicForm({ name: topic.name, description: topic.description || '', fase: topic.fase || 1 })}
              >
                Editar tópico
              </Button>
            )}
            <ListenButton kind="topic" id={topic.id} available={!!topic.onePage} />
            {!tutorOpen && (
              <Button variant="accent" icon={Sparkles} onClick={() => setTutorOpen(true)}>Tutor</Button>
            )}
          </>
        }
      />

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { value: 'lessons', label: 'Aulas', icon: FileText, count: lessons.length, tour: 'topico-aulas-tab' },
          { value: 'tasks', label: 'Tarefas', icon: ListChecks, count: tasks.length, tour: 'topico-tarefas' },
          { value: 'artifacts', label: 'Artefatos', icon: Layers },
          { value: 'articles', label: 'Artigos', icon: Newspaper, count: topicArticles.length },
        ]}
      />

      <div className="topic-panel">
        {activeTab === 'lessons' && (
          <>
            <div className="topic-toolbar">
              <div className="topic-toolbar__progress">
                <span className="topic-toolbar__count">
                  <strong>{completedCount}</strong>/{lessons.length} aulas concluídas
                </span>
                <ProgressBar
                  value={lessons.length ? (completedCount / lessons.length) * 100 : 0}
                  label="Aulas concluídas no tópico"
                />
              </div>
              {isAdmin && <Button icon={Plus} onClick={() => setShowAddLesson(true)} data-tour="nova-aula">Nova aula</Button>}
            </div>

            {lessons.length === 0 ? (
              <EmptyState
                file="aulas.xml"
                title="Nenhuma aula criada ainda"
                description={isAdmin ? 'Crie a primeira aula para começar a montar este tópico.' : 'O conteúdo deste tópico ainda está sendo preparado.'}
                action={isAdmin && <Button icon={Plus} onClick={() => setShowAddLesson(true)}>Nova aula</Button>}
              />
            ) : (
              <ol className="lesson-list" data-tour="topico-aulas">
                {lessons.map((lesson, index) => {
                  const done = !!lessonProgress[lesson.id];
                  return (
                    <li key={lesson.id} className={`lesson-row ${done ? 'lesson-row--done' : ''}`} data-tour={index === 0 ? 'aula-item' : undefined}>
                      <button
                        type="button"
                        className="lesson-row__check"
                        onClick={(e) => handleToggleProgress(e, lesson.id)}
                        title={done ? 'Marcar como pendente' : 'Marcar como concluída'}
                        aria-label={done ? `Marcar "${lesson.name}" como pendente` : `Marcar "${lesson.name}" como concluída`}
                        aria-pressed={done}
                      >
                        {done ? <Check size={20} strokeWidth={3} /> : String(index + 1).padStart(2, '0')}
                      </button>
                      <button
                        type="button"
                        className="lesson-row__main"
                        onClick={() => navigate(buildPath(`/estudo/${topicId}/aula/${lesson.id}`))}
                      >
                        <span className="lesson-row__text">
                          <span className="lesson-row__name">{lesson.name}</span>
                          {lesson.description && <span className="lesson-row__desc">{lesson.description}</span>}
                          <span className="lesson-row__tags">
                            {lesson.luriSummary && <Badge tone="lime" icon={NotebookText}>Resumo</Badge>}
                            {lesson.pdfPath && <Badge tone="navy" icon={FileText}>PDF</Badge>}
                            {lesson.contentMarkdown && <Badge tone="navy" icon={FileCode}>Conteúdo</Badge>}
                            {lesson.transcription && <Badge tone="navy" icon={MessageSquareText}>Transcrição</Badge>}
                            {lesson.durationMinutes && <Badge tone="neutral" icon={Clock}>{lesson.durationMinutes} min</Badge>}
                          </span>
                        </span>
                        <ChevronRight size={20} className="lesson-row__go" aria-hidden="true" />
                      </button>
                      {isAdmin && (
                        <span className="lesson-row__admin">
                          <button
                            type="button"
                            className="ss-iconbtn"
                            onClick={() => setLessonForm({
                              id: lesson.id, name: lesson.name, description: lesson.description || '',
                              durationMinutes: lesson.durationMinutes ?? '',
                            })}
                            aria-label={`Editar aula "${lesson.name}"`}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            className="ss-iconbtn ss-iconbtn--danger"
                            onClick={() => setLessonToDelete(lesson)}
                            data-tour="excluir"
                            aria-label={`Excluir aula "${lesson.name}"`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </>
        )}

        {activeTab === 'tasks' && (
          <>
            <div className="topic-toolbar">
              <div className="topic-toolbar__progress">
                <span className="topic-toolbar__count">Tarefas práticas deste tópico</span>
              </div>
              {isAdmin && <Button icon={Plus} onClick={() => setShowAddTask(true)} data-tour="nova-tarefa">Nova tarefa</Button>}
            </div>

            {tasks.length === 0 ? (
              <EmptyState
                file="tarefas.xml"
                title="Nenhuma tarefa criada ainda"
                description="Use tarefas para praticar o que foi estudado neste tópico."
                action={isAdmin && <Button icon={Plus} onClick={() => setShowAddTask(true)}>Nova tarefa</Button>}
              />
            ) : (
              <ul className="task-list">
                {tasks.map(task => {
                  const priority = PRIORITIES[task.priority] || PRIORITIES[0];
                  return (
                    <li key={task.id} className={`task-row task-row--${task.status}`}>
                      <Badge tone={priority.tone}>{priority.label}</Badge>
                      <div className="task-row__text">
                        <span className="task-row__title">{task.title}</span>
                        {task.description && <span className="task-row__desc">{task.description}</span>}
                        {task.dueDate && <span className="task-row__due"><Clock size={13} aria-hidden="true" /> Prazo: {task.dueDate}</span>}
                      </div>
                      <div className="task-row__actions">
                        <select
                          className="ss-input task-row__status"
                          value={task.status}
                          onChange={(e) => handleTaskStatusChange(task.id, e.target.value)}
                          aria-label="Status da tarefa"
                        >
                          <option value="pending">Pendente</option>
                          <option value="in_progress">Em progresso</option>
                          <option value="completed">Concluída</option>
                        </select>
                        {isAdmin && (
                          <button
                            type="button"
                            className="ss-iconbtn ss-iconbtn--danger"
                            onClick={() => handleDeleteTask(task.id)}
                            aria-label={`Excluir tarefa "${task.title}"`}
                          >
                            <Trash2 size={17} />
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {activeTab === 'articles' && (
          <div className="topic-articles">
            <div className="topic-articles__bar">
              <span className="topic-articles__read">
                <strong>{topicArticles.filter(a => a.read).length}</strong>/{topicArticles.length} artigos lidos
              </span>
              {!user?.demo && (
                <Button icon={PenLine} onClick={() => navigate('/artigos/novo', {
                  state: {
                    tags: [topic.name],
                    links: [{
                      kind: 'topic', targetId: topic.id, name: topic.name, topicId: topic.id, topicName: topic.name,
                      fase: topic.fase || 1, courseName: currentCourse?.name, courseSlug, courseColor: currentCourse?.color,
                    }],
                  },
                })}>
                  Escrever sobre este tópico
                </Button>
              )}
            </div>
            {topicArticles.length === 0 ? (
              <EmptyState
                file="artigos.xml"
                title="Nenhum artigo sobre este tópico ainda"
                description="Artigos com temas ligados a este tópico aparecem aqui automaticamente."
              />
            ) : (
              <div className="topic-articles__list">
                {topicArticles.map(a => (
                  <button key={a.id} type="button" className="topic-article" onClick={() => navigate(`/artigos/${a.slug}`)}>
                    <ArticleCover article={a} className="topic-article__cover" />
                    <span className="topic-article__text">
                      <span className="topic-article__title">{a.title}</span>
                      <span className="topic-article__by">{a.author?.name} · {shortDate(a.publishedAt)}</span>
                      <span className="topic-article__badges">
                        {a.read && <ReadBadge />}
                        {a.lesson != null && <Badge tone="navy" icon={FileText}>Aula {a.lesson}</Badge>}
                        <Badge tone="neutral" icon={Clock}>{a.readingMinutes} min</Badge>
                      </span>
                    </span>
                    <ChevronRight size={18} aria-hidden="true" className="topic-article__go" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'artifacts' && (
          <div className="artifact-grid">
            <Card padding="lg" className="artifact artifact--wide">
              <div className="artifact__head">
                <IconBadge icon={ScrollText} size={44} />
                <div className="artifact__title">
                  <CodeTag>OnePage</CodeTag>
                  <h3>One-Page do tópico</h3>
                </div>
                <div className="artifact__actions">
                  <Button size="sm" variant="accent" icon={Sparkles} onClick={() => setShowQuestions(true)}>
                    Gerar questões
                  </Button>
                  {renderEditActions('onePage')}
                </div>
              </div>
              {renderTextArtifact('onePage', 'Nenhum one-page criado ainda.', 'Cole ou digite o resumo one-page do tópico...', 12)}
            </Card>

            <Card padding="lg" className="artifact artifact--wide">
              <div className="artifact__head">
                <IconBadge icon={Layers} size={44} />
                <div className="artifact__title">
                  <CodeTag>Flashcards</CodeTag>
                  <h3>Flashcards</h3>
                </div>
                <div className="artifact__actions">{renderEditActions('flashcards', 'Editar JSON')}</div>
              </div>
              {editingArtifact === 'flashcards' ? (
                <textarea
                  className="ss-textarea"
                  value={artifactValue}
                  onChange={(e) => setArtifactValue(e.target.value)}
                  rows={12}
                  placeholder='[{"front": "Pergunta", "back": "Resposta"}, ...]'
                />
              ) : (
                <FlashcardViewer
                  flashcards={flashcards}
                  topicName={topic.name}
                  topicId={topic.id}
                  courseSlug={courseSlug}
                />
              )}
            </Card>

            <Card padding="lg" className="artifact">
              <div className="artifact__head">
                <IconBadge icon={CircleHelp} size={44} />
                <div className="artifact__title">
                  <CodeTag>Prova</CodeTag>
                  <h3>10 perguntas de prova</h3>
                </div>
                <div className="artifact__actions">
                  <Button
                    size="sm"
                    variant="accent"
                    icon={Sparkles}
                    onClick={() => setShowQuestions(true)}
                    aria-label="Gerar questões com IA"
                  />
                  {renderEditActions('insights')}
                </div>
              </div>
              {renderTextArtifact('insights', 'Nenhuma pergunta de prova criada ainda.', '# 10 Perguntas de Prova\n\n## 1. Pergunta...\n...', 15)}
            </Card>

            <Card padding="lg" className="artifact">
              <div className="artifact__head">
                <IconBadge icon={Link2} size={44} />
                <div className="artifact__title">
                  <CodeTag>Conexoes</CodeTag>
                  <h3>Interseções e conexões</h3>
                </div>
                <div className="artifact__actions">{renderEditActions('intersections')}</div>
              </div>
              {renderTextArtifact('intersections', 'Nenhuma interseção criada ainda.', 'Conexões entre este tópico e outros...', 10)}
            </Card>

            <Card padding="lg" className="artifact">
              <div className="artifact__head">
                <IconBadge icon={Video} size={44} />
                <div className="artifact__title">
                  <CodeTag>Video</CodeTag>
                  <h3>Video overview</h3>
                </div>
                <div className="artifact__actions">
                  {isAdmin && (
                    <FileButton size="sm" icon={Upload} accept="video/*" onChange={handleVideoUpload}>
                      {topic.videoPath ? 'Trocar' : 'Adicionar'}
                    </FileButton>
                  )}
                </div>
              </div>
              {topic.videoPath ? (
                <video controls className="artifact__video" src={mediaService.getVideoUrl(topic.videoPath)}>
                  Seu navegador não suporta a reprodução de vídeos.
                </video>
              ) : (
                <p className="artifact__placeholder">Nenhum vídeo adicionado ainda.</p>
              )}
            </Card>

            <Card padding="lg" className="artifact">
              <div className="artifact__head">
                <IconBadge icon={Headphones} size={44} />
                <div className="artifact__title">
                  <CodeTag>Audio</CodeTag>
                  <h3>Audio overview</h3>
                </div>
                <div className="artifact__actions">
                  {isAdmin && (
                    <FileButton size="sm" icon={Upload} accept="audio/*" onChange={handleAudioUpload}>
                      {topic.audioPath ? 'Trocar' : 'Adicionar'}
                    </FileButton>
                  )}
                </div>
              </div>
              {topic.audioPath ? (
                <audio controls className="artifact__audio" src={mediaService.getAudioUrl(topic.audioPath)}>
                  Seu navegador não suporta a reprodução de áudios.
                </audio>
              ) : (
                <p className="artifact__placeholder">Nenhum áudio adicionado ainda.</p>
              )}
            </Card>
          </div>
        )}
      </div>

      <PagerNav
        prev={prevTopic && {
          hint: 'Tópico anterior',
          label: prevTopic.name,
          onClick: () => navigate(buildPath(`/estudo/${prevTopic.id}`)),
        }}
        next={nextTopic ? {
          hint: 'Próximo tópico',
          label: nextTopic.name,
          onClick: () => navigate(buildPath(`/estudo/${nextTopic.id}`)),
        } : {
          hint: 'Último tópico',
          label: 'Concluir curso',
          done: true,
          onClick: () => navigate(buildPath('/estudo')),
        }}
      />
      </div>

      <Modal
        open={showAddLesson}
        onClose={() => setShowAddLesson(false)}
        title="Nova aula"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddLesson(false)}>Cancelar</Button>
            <Button onClick={handleAddLesson} disabled={!newLesson.name.trim()}>Criar aula</Button>
          </>
        }
      >
        <Field label="Nome da aula" required>
          <input
            type="text"
            value={newLesson.name}
            onChange={(e) => setNewLesson({ ...newLesson, name: e.target.value })}
            placeholder="Ex: Introdução ao Item Type"
            autoFocus
          />
        </Field>
        <Field label="Descrição">
          <textarea
            value={newLesson.description}
            onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })}
            placeholder="Breve descrição da aula..."
            rows={3}
          />
        </Field>
      </Modal>

      <Modal
        open={showAddTask}
        onClose={() => setShowAddTask(false)}
        title="Nova tarefa"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddTask(false)}>Cancelar</Button>
            <Button onClick={handleAddTask} disabled={!newTask.title.trim()}>Criar tarefa</Button>
          </>
        }
      >
        <Field label="Título" required>
          <input
            type="text"
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            placeholder="Ex: Implementar exercício prático"
            autoFocus
          />
        </Field>
        <Field label="Descrição">
          <textarea
            value={newTask.description}
            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
            placeholder="Detalhes da tarefa..."
            rows={3}
          />
        </Field>
        <Field label="Prioridade">
          <select
            value={newTask.priority}
            onChange={(e) => setNewTask({ ...newTask, priority: parseInt(e.target.value) })}
          >
            <option value={0}>Baixa</option>
            <option value={1}>Média</option>
            <option value={2}>Alta</option>
          </select>
        </Field>
      </Modal>

      <Modal
        open={!!topicForm}
        onClose={() => setTopicForm(null)}
        title="Editar tópico"
        footer={
          <>
            <Button variant="ghost" icon={Trash2} className="topic-delete-btn" onClick={() => { setTopicForm(null); setConfirmDeleteTopic(true); }}>
              Excluir tópico
            </Button>
            <Button variant="outline" onClick={() => setTopicForm(null)}>Cancelar</Button>
            <Button onClick={saveTopicForm} disabled={!topicForm?.name?.trim()}>Salvar</Button>
          </>
        }
      >
        {topicForm && (
          <>
            <Field label="Fase" required>
              <select value={topicForm.fase} onChange={(e) => setTopicForm({ ...topicForm, fase: e.target.value })}>
                {getPhases().map(f => <option key={f.num} value={f.num}>Fase {f.num} - {f.name}</option>)}
              </select>
            </Field>
            <Field label="Nome do tópico" required>
              <input value={topicForm.name} onChange={(e) => setTopicForm({ ...topicForm, name: e.target.value })} autoFocus />
            </Field>
            <Field label="Descrição">
              <textarea rows={3} value={topicForm.description} onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })} />
            </Field>
          </>
        )}
      </Modal>

      <Modal
        open={confirmDeleteTopic}
        onClose={() => setConfirmDeleteTopic(false)}
        title="Excluir tópico?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDeleteTopic(false)}>Cancelar</Button>
            <Button variant="danger" icon={Trash2} onClick={deleteCurrentTopic}>Excluir tópico</Button>
          </>
        }
      >
        <p>
          <strong>{topic.name}</strong> e as aulas dele deixam de aparecer para todos os usuários.
          O progresso já registrado continua guardado.
        </p>
      </Modal>

      <Modal
        open={!!lessonForm}
        onClose={() => setLessonForm(null)}
        title="Editar aula"
        footer={
          <>
            <Button variant="outline" onClick={() => setLessonForm(null)}>Cancelar</Button>
            <Button onClick={saveLessonForm} disabled={!lessonForm?.name?.trim()}>Salvar</Button>
          </>
        }
      >
        {lessonForm && (
          <>
            <Field label="Nome da aula" required>
              <input value={lessonForm.name} onChange={(e) => setLessonForm({ ...lessonForm, name: e.target.value })} autoFocus />
            </Field>
            <Field label="Descrição">
              <textarea rows={3} value={lessonForm.description} onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })} />
            </Field>
            <Field label="Duração (minutos)">
              <input type="number" min="1" max="600" value={lessonForm.durationMinutes}
                     onChange={(e) => setLessonForm({ ...lessonForm, durationMinutes: e.target.value })} />
            </Field>
          </>
        )}
      </Modal>

      <Modal
        open={!!lessonToDelete}
        onClose={() => setLessonToDelete(null)}
        title="Excluir aula?"
        footer={
          <>
            <Button variant="outline" onClick={() => setLessonToDelete(null)}>Cancelar</Button>
            <Button variant="danger" icon={Trash2} onClick={deleteLesson}>Excluir aula</Button>
          </>
        }
      >
        <p><strong>{lessonToDelete?.name}</strong> deixa de aparecer para todos os usuários.</p>
      </Modal>

      {showQuestions && (
        <AIQuestions open={showQuestions} onClose={() => setShowQuestions(false)} topicId={topic.id} topicName={topic.name} courseSlug={courseSlug} />
      )}
      <SelectionActions containerRef={pageRef} onAction={onSelection} />
      <AITutor
        ctx={{ topicId: topic.id }}
        title={topic.name}
        reading={`este tópico · ${[topic.onePage && 'one-page', topic.insights && 'insights', 'aulas'].filter(Boolean).join(', ')}`}
        open={tutorOpen}
        onOpenChange={setTutorOpen}
        request={tutorRequest}
        onSource={onSource}
        hasCode={/```/.test(topic.onePage || '')}
      />
    </AppShell>
  );
}

// Aceita os formatos de flashcard já gravados: [{front, back}], [{q, a}] ou { flashcards: [...] }
function parseFlashcards(raw) {
  try {
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const cards = Array.isArray(parsed) ? parsed : (parsed.flashcards || []);
    return cards.map(card => ({
      front: card.front || card.q || card.question || '',
      back: card.back || card.a || card.answer || '',
    }));
  } catch {
    return [];
  }
}

export default TopicView;
